import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Button,
  CircularProgress,
  IconButton
} from "@mui/material";
import { Close, Delete } from "@mui/icons-material";
import styled from "@emotion/styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faUpload } from "@fortawesome/free-solid-svg-icons";
import { FileWithMetadata, UploadProgress, MAX_FILES } from '../../../services/documentService';
import { formatFileSize } from '../../../services/fileValidationService';
import { getFileIcon, getFileTypeColor } from '../../../utils/fileUtils';
import { GuardedButton } from '../../common/guarded-button';

// Theme colors
const COLORS = {
  white: "#fff",
  primary: "hsl(19 101% 51% / 1)",
  primaryHover: "hsl(19 101% 41% / 1)",
  primaryLight: "hsl(19 101% 61% / 1)",
};

const FileItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  padding: "12px 16px",
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  marginBottom: "8px",
  backgroundColor: "white",
  "&:hover": {
    backgroundColor: "#f8f9fa"
  }
});

interface FileListProps {
  files: FileWithMetadata[];
  progress: UploadProgress;
  uploading: boolean;
  onRemoveFile: (fileId: string) => void;
  onClearAllFiles: () => void;
  onUploadFiles: () => void;
}

export const FileList: React.FC<FileListProps> = ({
  files,
  progress,
  uploading,
  onRemoveFile,
  onClearAllFiles,
  onUploadFiles
}) => {
  if (files.length === 0) {
    return null;
  }

  const getStatusIcon = (fileWithMeta: FileWithMetadata) => {
    switch (fileWithMeta.status) {
      case 'success':
        return <FontAwesomeIcon icon={faCheckCircle} color="#4caf50" />;
      case 'error':
        return <Typography variant="caption" color="error">Error</Typography>;
      case 'uploading':
        return <CircularProgress size={20} sx={{ color: COLORS.primary }} />;
      default:
        return null;
    }
  };

  const totalFilesSize = files.reduce((acc, fileWithMeta) => acc + fileWithMeta.file.size, 0);
  const canUpload = files.length > 0 && files.every(f => f.status === 'pending' || f.status === 'error');

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Files ({files.length}/{MAX_FILES})
        </Typography>
        <Button
          startIcon={<Delete />}
          onClick={onClearAllFiles}
          disabled={uploading}
          size="small"
          sx={{
            color: '#d32f2f',
            '&:hover': {
              backgroundColor: '#d32f2f10',
            }
          }}
        >
          Clear All
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Total size: {formatFileSize(totalFilesSize)}
      </Typography>

      {files.map((fileWithMeta) => (
        <FileItem key={fileWithMeta.id}>
          <FontAwesomeIcon
            icon={getFileIcon(fileWithMeta.file.type)}
            color={getFileTypeColor(fileWithMeta.file.type)}
            style={{ marginRight: '12px', fontSize: '20px' }}
          />

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body1" noWrap title={fileWithMeta.file.name}>
              {fileWithMeta.file.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatFileSize(fileWithMeta.file.size)}
              {fileWithMeta.documentId && ` • ID: ${fileWithMeta.documentId}`}
            </Typography>

            {fileWithMeta.status === 'error' && (
              <Typography variant="caption" color="error">
                {fileWithMeta.error}
              </Typography>
            )}

            {fileWithMeta.status === 'uploading' && progress[fileWithMeta.file.name] !== undefined && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress[fileWithMeta.file.name] || 0}
                  sx={{ height: 6, borderRadius: 3 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {Math.round(progress[fileWithMeta.file.name] || 0)}%
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon(fileWithMeta)}
            {!uploading && (
              <IconButton
                onClick={() => onRemoveFile(fileWithMeta.id)}
                size="small"
                aria-label={`Remove ${fileWithMeta.file.name}`}
              >
                <Close />
              </IconButton>
            )}
          </Box>
        </FileItem>
      ))}

      {/* Upload Button */}
      {!uploading && canUpload && (
        <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            onClick={onClearAllFiles}
            sx={{
              borderColor: COLORS.primary,
              color: COLORS.primary,
              '&:hover': {
                borderColor: COLORS.primaryHover,
                backgroundColor: `${COLORS.primary}10`,
              }
            }}
          >
            Cancel
          </Button>
          <GuardedButton
            feature="documents-upload"
            action="create"
            variant="contained"
            startIcon={<FontAwesomeIcon icon={faUpload} />}
            onClick={onUploadFiles}
            sx={{ 
              minWidth: 140,
              backgroundColor: COLORS.primary,
              color: COLORS.white,
              '&:hover': {
                backgroundColor: COLORS.primaryHover,
              }
            }}
          >
            Upload {files.length} File{files.length !== 1 ? 's' : ''}
          </GuardedButton>
        </Box>
      )}

      {uploading && (
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <CircularProgress sx={{ color: COLORS.primary }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Uploading files...
          </Typography>
        </Box>
      )}
    </Box>
  );
};